import { useEffect, useEffectEvent, useRef, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useMutation } from '@tanstack/react-query';

import Constants from 'constants/index';

import { updateUserComplianceCourseDurationMutation } from '@/_generated/api-clients/content';
import { shouldSkipHeartbeatErrorReporting } from '@/routes/TrainingPlans/hooks/heartbeat-error';

/** Used to track time spent on compliance courses. */
export const useComplianceTimer = (courseId: string) => {
  const updateComplianceHeartbeat = useMutation(
    updateUserComplianceCourseDurationMutation(),
  );
  const [timerIsRunning, setTimerIsRunning] = useState(false);
  const roundUpTimerRef = useRef(false);

  const logTimeViewed = useEffectEvent((milliSeconds: number) => {
    const seconds = milliSeconds / 1000;
    updateComplianceHeartbeat
      .mutateAsync({
        path: {
          pathwayCourseId: courseId,
        },
        body: {
          interval: seconds,
        },
      })
      .then()
      .catch((err: unknown) => {
        if (shouldSkipHeartbeatErrorReporting(err)) {
          return;
        }
        datadogRum.addError(
          new Error(err instanceof Error ? err.message : 'Unknown error'),
          {
            context: JSON.stringify(err),
          },
        );
      });
  });

  useEffect(() => {
    let logIntervalId: NodeJS.Timeout | null = null;
    let roundIntervalId: NodeJS.Timeout | null = null;

    if (timerIsRunning && courseId) {
      logIntervalId = setInterval(() => {
        roundUpTimerRef.current = !roundUpTimerRef.current;
      }, Constants.VIEW_TIME_DEFAULTS.POLL_TIME_ROUNDUP);
      roundIntervalId = setInterval(() => {
        logTimeViewed(Constants.VIEW_TIME_DEFAULTS.POLL_TIME_DEFAULT);
      }, Constants.VIEW_TIME_DEFAULTS.POLL_TIME_DEFAULT);
    }
    return () => {
      if (logIntervalId) {
        clearInterval(logIntervalId);
      }
      if (roundIntervalId) {
        clearInterval(roundIntervalId);
      }
      if (roundUpTimerRef.current && timerIsRunning && courseId) {
        logTimeViewed(Constants.VIEW_TIME_DEFAULTS.POLL_TIME_DEFAULT);
        roundUpTimerRef.current = false;
      }
    };
  }, [courseId, timerIsRunning]);

  useEffect(
    () => () => {
      setTimerIsRunning(false);
    },
    [],
  );

  const startTimer = () => {
    setTimerIsRunning(true);
  };

  const stopTimer = () => {
    setTimerIsRunning(false);
  };

  return { startTimer, stopTimer };
};
